// Iter-193q — Cabinet profitability KPI cards.
// Extracted from CabinetExpenses.jsx so the same widget can be surfaced on the
// global Profitability dashboard (where the user expects to consult portfolio
// + cabinet KPIs in one place).
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CabinetKpis() {
  const [data, setData] = useState({ month: null, "6m": null, "12m": null });
  useEffect(() => {
    let alive = true;
    Promise.all(
      ["month", "6m", "12m"].map((p) =>
        api
          .get(`/cabinet-expenses/profitability?period=${p}`)
          .then((r) => [p, r.data])
          .catch(() => [p, null])
      )
    ).then((rows) => {
      if (!alive) return;
      const next = { month: null, "6m": null, "12m": null };
      rows.forEach(([k, v]) => {
        next[k] = v;
      });
      setData(next);
    });
    return () => {
      alive = false;
    };
  }, []);

  const cards = [
    { key: "month", label: "Ce mois" },
    { key: "6m", label: "6 derniers mois" },
    { key: "12m", label: "12 derniers mois" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cabinet-kpis">
      {cards.map(({ key, label }) => {
        const d = data[key];
        const profit = d?.profit_net_mad;
        const positive = profit != null && profit >= 0;
        return (
          <div
            key={key}
            className="bg-white border border-zinc-200 rounded-lg p-4"
            data-testid={`cabinet-kpi-${key}`}
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
              {label}
            </div>
            {d ? (
              <div className="space-y-1.5">
                <div
                  className={`text-2xl font-bold font-mono ${
                    positive ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  {fmt(profit)} <span className="text-xs text-zinc-500">MAD</span>
                </div>
                <div className="text-[10px] text-zinc-500 font-mono">PROFIT NET</div>
                <div className="border-t border-zinc-100 mt-2 pt-2 text-[10px] space-y-0.5 font-mono">
                  <div className="flex justify-between">
                    <span className="text-emerald-700">+ Revenus</span>
                    <span>{fmt(d.revenue_mad)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>− Coûts missions</span>
                    <span>{fmt(d.mission_costs_mad)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>− Dépenses cabinet</span>
                    <span>{fmt(d.cabinet_expenses_mad)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>− NDF non refac.</span>
                    <span>{fmt(d.personal_ndf_non_billable_mad)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>− NDF refac. non fact.</span>
                    <span>{fmt(d.personal_ndf_billable_unbilled_mad)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <Loader2 size={14} className="animate-spin text-zinc-400" />
            )}
          </div>
        );
      })}
    </div>
  );
}
